
import { useState, useEffect } from "react";
import { Canvas } from "fabric";
import { CustomFabricObject } from "@/types/fabric";

interface ToolUsage {
  draw: boolean;
  incision: boolean;
  ruler: boolean;
  angle: boolean;
}

export const useToolUsage = (canvas: Canvas | null, activeTool: string | null) => {
  const [usedTools, setUsedTools] = useState<ToolUsage>({
    draw: false,
    incision: false,
    ruler: false,
    angle: false
  });

  useEffect(() => {
    if (!canvas) return;

    const checkRemainingObjects = () => {
      const objects = canvas.getObjects() as CustomFabricObject[];
      const hasDrawing = objects.some(obj => obj.data?.type === 'drawing');
      const hasIncision = objects.some(obj => (
        obj.data?.type === 'incision' || 
        obj.data?.type === 'scalpel'
      ));
      const hasRuler = objects.some(obj => obj.data?.isPermanent || obj.data?.type === 'ruler');
      const hasAngle = objects.some(obj => obj.data?.type === 'angleMeasurement');

      console.log('Checking remaining objects:', { hasDrawing, hasIncision, hasRuler, hasAngle });

      setUsedTools(prev => ({
        ...prev,
        draw: hasDrawing,
        incision: hasIncision,
        ruler: hasRuler,
        angle: hasAngle
      }));
    };

    const handlePathCreated = (e: { path: CustomFabricObject }) => {
      const path = e.path;
      if (!path) return;

      // Check if it's a drawing path - REMOVED the !usedTools.draw condition
      if (activeTool === 'draw') {
        if (path.width && path.height && (path.width > 10 || path.height > 10)) {
          console.log('Drawing tool used');
          path.data = { type: 'drawing' };
          setUsedTools(prev => ({ ...prev, draw: true }));
        }
      }

      // Check if it's an incision or scalpel path - REMOVED the !usedTools.incision condition
      if (activeTool === 'incision' || activeTool === 'scalpel') {
        if (path.width && path.height && (path.width > 10 || path.height > 10)) {
          console.log(`${activeTool} tool used`);
          path.data = { type: activeTool as 'incision' | 'scalpel' }; 
          setUsedTools(prev => ({ ...prev, incision: true }));
        }
      }
    };

    // Handle incision template added event
    const handleIncisionTemplateAdded = () => {
      console.log('Incision template added');
      setUsedTools(prev => ({ ...prev, incision: true }));
    };

    // Handle measurement completion
    const handleObjectAdded = (e: { target: CustomFabricObject }) => {
      const obj = e.target;
      if (!obj) return;

      console.log('Object added:', obj.data);

      // Check for ruler measurements - REMOVED the !usedTools.ruler condition
      if (activeTool === 'ruler') {
        if (obj.data?.isPermanent || obj.data?.type === 'ruler') {
          console.log('Ruler measurement completed');
          setUsedTools(prev => ({ ...prev, ruler: true }));
        }
      }

      // Check for angle measurements - REMOVED the !usedTools.angle condition
      if (activeTool === 'angle') {
        if (obj.data?.type === 'angleMeasurement') {
          console.log('Angle measurement completed');
          setUsedTools(prev => ({ ...prev, angle: true }));
        }
      }

      // Check if added object is an incision or scalpel - REMOVED the !usedTools.incision condition
      if (obj.data?.type === 'incision' || obj.data?.type === 'scalpel') {
        console.log('Incision or scalpel shape added');
        setUsedTools(prev => ({ ...prev, incision: true }));
      }
    };

    const handleObjectRemoved = (e: { target: CustomFabricObject }) => {
      const removedObj = e.target;
      if (!removedObj) return;

      console.log('Object removed:', removedObj.data);
      
      // After removal, check what objects remain on canvas
      setTimeout(() => {
        checkRemainingObjects();
      }, 0);
    };

    canvas.on('path:created', handlePathCreated);
    canvas.on('object:added', handleObjectAdded);
    canvas.on('object:removed', handleObjectRemoved);
    
    // Use 'as any' to bypass TypeScript checking for our custom event
    (canvas as any).on('incision:template:added', handleIncisionTemplateAdded);

    return () => {
      canvas.off('path:created', handlePathCreated);
      canvas.off('object:added', handleObjectAdded);
      canvas.off('object:removed', handleObjectRemoved);
      
      // Clean up our custom event listener
      (canvas as any).off('incision:template:added', handleIncisionTemplateAdded);
    };
  }, [canvas, activeTool, usedTools]);

  const markMeasurementUsed = (tool: 'ruler' | 'angle') => {
    console.log(`Marking ${tool} as used`);
    setUsedTools(prev => {
      const newState = { ...prev, [tool]: true };
      console.log('Tool usage updated:', newState);
      return newState;
    });
  };

  return {
    usedTools,
    markMeasurementUsed
  };
};
